import moment from "moment";

export const RESOLVED_OUTCOME = "Resolved";

const LINE_BREAK = "\n";
const SECTION_BREAK = "\n\n";
const SYMPTOM_SEPARATOR = " & ";
const PRESENT_TENSE = { presented: "has", classified: "is being" };
const PAST_TENSE = { presented: "had", classified: "was" };
const CONFIRMED = "confirmed";
const SUSPECTED = "suspected";
const LABS_HEADER = "Linked Lab Results:";
const TREATMENTS_HEADER = "Treatment Details:";
const INFECTION_NOTE_PRECAUTIONS_HEADER = "Precautions";
const RESOLUTION_PRECAUTIONS_HEADER = "Precautions assigned to case are now resolved";
const CASE_SUMMARY_HEADER = "Case Summary:";
const REFER_TO_HCX = "Refer to HealthConnex for more information.";

// Templated note sentences. Empty optional parts (end dates, sub-type) are passed as "".
const NOTE_COPY = {
  symptoms: (name, verb, symptoms, onset) => `${name} ${verb} presented with ${symptoms} symptoms onset on ${onset}.`,
  classification: (verb, certainty, site, subtype) => `This ${verb} classified as a ${certainty} ${site} ${subtype} infection.`,
  lab: (collected, result, organism) => `Specimen collected on ${collected}. A ${result} ${organism} result was determined.`,
  treatment: (displayText, start, endPart) => `${displayText}. Treatment started on ${start}${endPart}`,
  ongoingEnd: end => `, ending on ${end}`,
  resolvedEnd: end => ` and ended ${end}`,
  notification: (displayText, datePart) => `${displayText}${datePart}`,
  notifiedDate: date => ` (${date})`,
  precaution: (name, start, endPart) => `${name}: started on ${start}${endPart}`,
  caseOutcome: (outcome, date) => `This infection case has an outcome of ${outcome} on ${date}`,
  resolvedHeadline: (name, site, date) => `${name}'s ${site} Infection has resolved on ${date}.`,
  outcomeHeadline: (name, site, outcome, subTypePart, date) => `${name}'s ${site} infection case has an outcome of ${outcome}${subTypePart} on ${date}.`,
  outcomeSubType: subType => ` / ${subType}`
};

const printList = arr => arr.map(i => i.displayText).join(SYMPTOM_SEPARATOR);
const datePrinter = dateFormat => timestamp => (timestamp ? moment(timestamp).format(dateFormat) : "");
const clientName = client => `${client.firstName} ${client.lastName}`;

// Shared case-summary pieces; each returns an array of lines ([] when the section is empty).
const optional = (value, format) => (value ? format(value) : "");
const symptomLines = (client, data, tense, printDate) => [
  NOTE_COPY.symptoms(clientName(client), tense.presented, printList(data.symptoms || []), printDate(data.onSetDate || data.onsetDate)),
  NOTE_COPY.classification(tense.classified, data.confirmed ? CONFIRMED : SUSPECTED, data.infectionSite, data.subtypeString || "")
];
const labLines = (labs, printDate) =>
  labs.length ? [LABS_HEADER, ...labs.map(lab => NOTE_COPY.lab(printDate(lab.collectionDate), lab.result, lab.organism))] : [];
const treatmentLines = (treatments, printDate) =>
  treatments.length
    ? [TREATMENTS_HEADER, ...treatments.map(t => NOTE_COPY.treatment(t.displayText, printDate(t.startDate), optional(t.endDate, d => NOTE_COPY.ongoingEnd(printDate(d)))))]
    : [];
const notificationLines = (data, printDate) =>
  (data.notifications || []).filter(n => n.notified).map(n => NOTE_COPY.notification(n.displayText, optional(n.notifiedDate, d => NOTE_COPY.notifiedDate(printDate(d)))));
const precautionLines = (header, endCopy, precautions, precautionsLookup, printDate) => {
  const known = precautions.filter(p => precautionsLookup[p.precautionId]);
  return known.length
    ? [header, ...known.map(p => NOTE_COPY.precaution(precautionsLookup[p.precautionId].displayText, printDate(p.startDate), optional(p.endDate, d => endCopy(printDate(d)))))]
    : [];
};

/**
 * Builds the human-readable infection note shown by "Generate Infection Note" and pre-filled in the
 * PointClickCare progress-note dialog. Pure: no HTTP, no component state.
 * @param {object} p
 * @param {object} p.client            infection case client (firstName, lastName, precautionDetails[])
 * @param {object} p.infectionCase     symptoms[], onSetDate, confirmed, infectionSite, subtypeString, notifications[], resolved, outcome, resolutionDate
 * @param {Array}  p.labs              [{ collectionDate, result, organism }] — pass [] when none
 * @param {Array}  p.treatments        [{ displayText, startDate, endDate }] — pass [] when none
 * @param {object} p.precautionsLookup { [precautionId]: { displayText } }
 * @param {string} p.dateFormat        moment format string (this.$configStore.dateFormat())
 */
export function buildInfectionNoteText({ client, infectionCase, labs = [], treatments = [], precautionsLookup = {}, dateFormat }) {
  const printDate = datePrinter(dateFormat);
  const data = infectionCase;
  const sections = [
    symptomLines(client, data, PRESENT_TENSE, printDate),
    labLines(labs, printDate),
    treatmentLines(treatments, printDate),
    notificationLines(data, printDate),
    precautionLines(INFECTION_NOTE_PRECAUTIONS_HEADER, NOTE_COPY.ongoingEnd, client.precautionDetails || [], precautionsLookup, printDate),
    data.resolved ? [NOTE_COPY.caseOutcome(data.outcome, printDate(data.resolutionDate))] : [],
    [REFER_TO_HCX]
  ];
  return sections
    .filter(lines => lines.length)
    .map(lines => lines.join(LINE_BREAK))
    .join(SECTION_BREAK);
}

/**
 * Builds the PointClickCare resolution note offered when a case is first resolved: the infection-note summary in
 * past tense, framed by the outcome line and the case's precautions with their end dates. Pure, like the above.
 * @param {object} p
 * @param {object} p.client            infection case client (firstName, lastName)
 * @param {object} p.infectionCase     as buildInfectionNoteText, plus outcomeSubType
 * @param {Array}  p.labs              [{ collectionDate, result, organism }] — pass [] when none
 * @param {Array}  p.treatments        [{ displayText, startDate, endDate }] — pass [] when none
 * @param {Array}  p.precautions       case-linked precaution_api rows [{ precautionId, startDate, endDate }]
 * @param {object} p.precautionsLookup { [precautionId]: { displayText } }
 * @param {string} p.dateFormat        moment format string (this.$configStore.dateFormat())
 */
export function buildResolutionNoteText({ client, infectionCase, labs = [], treatments = [], precautions = [], precautionsLookup = {}, dateFormat }) {
  const printDate = datePrinter(dateFormat);
  const data = infectionCase;
  const resolutionDate = printDate(data.resolutionDate);
  const name = clientName(client);
  const outcomeLine =
    data.outcome === RESOLVED_OUTCOME
      ? NOTE_COPY.resolvedHeadline(name, data.infectionSite, resolutionDate)
      : NOTE_COPY.outcomeHeadline(name, data.infectionSite, data.outcome, optional(data.outcomeSubType, NOTE_COPY.outcomeSubType), resolutionDate);
  const lines = [
    outcomeLine,
    CASE_SUMMARY_HEADER,
    ...symptomLines(client, data, PAST_TENSE, printDate),
    ...labLines(labs, printDate),
    ...treatmentLines(treatments, printDate),
    ...notificationLines(data, printDate),
    ...precautionLines(RESOLUTION_PRECAUTIONS_HEADER, NOTE_COPY.resolvedEnd, precautions, precautionsLookup, printDate)
  ];
  return [lines.join(LINE_BREAK), REFER_TO_HCX].join(SECTION_BREAK);
}
